import { supabase } from "@/integrations/supabase/client";

const ANON_KEY = "reqqy_anon_id";
const SESSION_KEY = "reqqy_session_id";
const ATTRIBUTION_KEY = "reqqy_attribution";

export interface Attribution {
  source?: string | null;
  content_id?: string | null; // struxy_content_piece_id
  slug?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  referrer?: string | null;
  blog_post_id?: string | null;
}

function getOrCreate(storage: Storage, key: string, factory: () => string): string {
  try {
    let v = storage.getItem(key);
    if (!v) { v = factory(); storage.setItem(key, v); }
    return v;
  } catch {
    return factory();
  }
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getAnonymousId(): string {
  if (typeof window === "undefined") return "";
  return getOrCreate(window.localStorage, ANON_KEY, uuid);
}

export function getSessionId(): string {
  if (typeof window === "undefined") return "";
  return getOrCreate(window.sessionStorage, SESSION_KEY, uuid);
}

export function captureAttributionFromUrl(): Attribution | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const source = params.get("source");
  const content_id = params.get("content_id");
  const slug = params.get("slug");
  const utm_source = params.get("utm_source");
  const utm_medium = params.get("utm_medium");
  const utm_campaign = params.get("utm_campaign");
  if (!source && !content_id && !utm_source && !utm_medium && !utm_campaign) return null;
  const attribution: Attribution = {
    source, content_id, slug, utm_source, utm_medium, utm_campaign,
    referrer: document.referrer || null,
  };
  try { window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution)); } catch { /* ignore */ }
  return attribution;
}

export function getStoredAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch { return null; }
}

export function clearStoredAttribution(): void {
  try { window.localStorage.removeItem(ATTRIBUTION_KEY); } catch { /* ignore */ }
}

export type EventName =
  | "blog_view"
  | "blog_cta_click"
  | "signup_started"
  | "signup_completed"
  | "login"
  | "generation_started"
  | "generation_completed"
  | "export_completed"
  | "payment_completed"
  | "app_opened"
  | "review_completed"
  | "project_opened"
  | "project_created"
  | "run_opened"
  | "run_created";

// Events that count as authenticated product usage
// (drives last_active_at and active_users metrics).
export const PRODUCT_ACTIVE_EVENTS: ReadonlySet<EventName> = new Set<EventName>([
  "login",
  "app_opened",
  "generation_started",
  "generation_completed",
  "export_completed",
  "review_completed",
  "project_opened",
  "project_created",
  "run_opened",
  "run_created",
]);

export async function trackEvent(
  eventName: EventName,
  metadata: Record<string, unknown> = {},
): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const attribution = getStoredAttribution() ?? {};
    const params = new URLSearchParams(window.location.search);
    const { data: userData } = await supabase.auth.getUser();
    const user_id = userData.user?.id ?? null;

    const row = {
      event_name: eventName,
      user_id,
      anonymous_id: getAnonymousId(),
      session_id: getSessionId(),
      blog_post_id: (metadata.blog_post_id as string) ?? attribution.blog_post_id ?? null,
      struxy_content_piece_id:
        (metadata.struxy_content_piece_id as string) ?? attribution.content_id ?? null,
      source: (metadata.source as string) ?? attribution.source ?? null,
      utm_source: params.get("utm_source") ?? attribution.utm_source ?? null,
      utm_medium: params.get("utm_medium") ?? attribution.utm_medium ?? null,
      utm_campaign: params.get("utm_campaign") ?? attribution.utm_campaign ?? null,
      referrer: document.referrer || attribution.referrer || null,
      path: window.location.pathname,
      metadata,
    };

    await supabase.from("analytics_events").insert([row as never]);

    // Update authenticated product activity summary
    if (user_id && PRODUCT_ACTIVE_EVENTS.has(eventName)) {
      try {
        await supabase.rpc("record_product_activity", {
          p_event_name: eventName,
          p_path: window.location.pathname,
          p_session_id: getSessionId(),
        });
      } catch (e) {
        if (typeof console !== "undefined") console.debug("record_product_activity failed", e);
      }
    }
  } catch (e) {
    // Never break the app on analytics failure
    if (typeof console !== "undefined") console.debug("trackEvent failed", e);
  }
}